import React, { useState } from 'react';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import BaseModal from '../../../../components/modals/BaseModal';
import { TextField, Button, FormControl, InputLabel, Select, MenuItem, InputAdornment } from '@mui/material';
import { addAsset } from '../../../../repository/AssetDAO';
import { useOrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import { ToastType, useToast } from '../../../../context/toast/ToastContext';
import { Timestamp } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useAssetConditions } from './AssetConditions';

const AddAssetModal = ({ show, onHide }) => {
    const [assetName, setAssetName] = useState("");
    const [assetCondition, setAssetCondition] = useState("");
    const [serialNumber, setSerialNumber] = useState("");
    const [employeeId, setEmployeeId] = useState("");
    const [purchaseDate, setPurchaseDate] = useState("");
    const [price, setPrice] = useState("");
    const [assetNameError, setAssetNameError] = useState(false);
    const { t } = useTranslation();
    const { getTranslatedConditions, getConditionKey, getTranslatedValue } = useAssetConditions(); 
    const { setLoadingData } = useLoadingContext();
    const { organization, organizationEmployees } = useOrganizationContext();
    const { showToast } = useToast();

    const handleSubmit = async () => {
        if (!assetName.trim()) {
            setAssetNameError(true);
            return;
        }
    
        setLoadingData(true);
        
        const item = {
            name: assetName,
            condition: assetCondition,
            serial_number: serialNumber,
            purchase_date: purchaseDate ? Timestamp.fromDate(new Date(purchaseDate)) : null,
            price: price,
            employee_id: employeeId,
        };
    
        try {
            const result = await addAsset(organization.id, item);
            
            if (result.success) {
                showToast(t(StringKeys.SUCCESSFULLY_ADDED_X, { x: `${assetName}` }), ToastType.SUCCESS);
                resetForm();
            } else {
                showToast(t(StringKeys.FAILED_TO_ADD_X, `${assetName}`), ToastType.DANGER);
            }
        } catch (error) {
            showToast(t(StringKeys.FAILED_TO_ADD_X, `${assetName}`), ToastType.DANGER);
        } finally {
            setLoadingData(false);
            onHide();
        }
    };    

    const resetForm = () => {
        setAssetName("");
        setAssetCondition("");
        setSerialNumber("");
        setPrice("");
        setPurchaseDate("");
        setEmployeeId("");
        setAssetNameError(false);
    };

    if (!organization) return null;

    const actions = (
        <Button onClick={handleSubmit}>{t(StringKeys.SUBMIT)}</Button>
    );

    return (
        <BaseModal
            open={show}
            onClose={() => {
                resetForm();
                onHide();
            }}
            title={t(StringKeys.ADD_ASSET)}
            actions={actions}
        >
            <TextField
                error={assetNameError}
                helperText={assetNameError ? t(StringKeys.ASSET_NAME_REQUIRED) : ""}
                autoFocus
                margin="dense"
                id="asset-name"
                label={t(StringKeys.NAME)}
                type="text"
                fullWidth
                variant="outlined"
                value={assetName}
                onChange={(e) => {
                    setAssetName(e.target.value);
                    if (assetNameError) {
                        setAssetNameError(false);
                    }
                }}
            />
            <TextField
                autoFocus
                margin="dense"
                id="serial-number"
                label={t(StringKeys.SERIAL_NUMBER)}
                type="text"
                fullWidth
                variant="outlined"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
            />
            <TextField
                margin="dense"
                id="purchase-date"
                label={t(StringKeys.PURCHASE_DATE)}
                type="date"
                fullWidth
                variant="outlined"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                InputLabelProps={{
                    shrink: true,
                }}
            />
            <TextField
                margin="dense"
                id="price"
                label={t(StringKeys.COST)}
                type="number"
                InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { 
                        min: "0", 
                        step: ".01",
                    },
                }}
                fullWidth
                variant="outlined"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
            />
            <FormControl fullWidth margin="dense">
                <InputLabel id="condition-label">{t(StringKeys.CONDITION)}</InputLabel>
                <Select
                    labelId="condition-label"
                    id="condition-select"
                    value={getTranslatedValue(assetCondition)}
                    label={t(StringKeys.CONDITION)}
                    onChange={(e) => setAssetCondition(getConditionKey(e.target.value))}
                >
                    <MenuItem value="">
                        <em>{t(StringKeys.NONE)}</em>
                    </MenuItem>
                    {getTranslatedConditions().map(({ key, value }) => (
                        <MenuItem key={key} value={value}>
                            {value}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
                <InputLabel id="posession">{t(StringKeys.EMPLOYEE_IN_POSSESSION)}</InputLabel>
                <Select
                    labelId="posession-label"
                    id="posession-select"
                    value={employeeId}
                    label={t(StringKeys.EMPLOYEE_IN_POSSESSION)}
                    onChange={(e) => setEmployeeId(e.target.value)}
                >
                    <MenuItem value="">
                        <em>{t(StringKeys.NONE)}</em>
                    </MenuItem>
                    {organizationEmployees.map((employee) => (
                        <MenuItem key={employee.id} value={employee.id}>
                            {employee.first_name} {employee.last_name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </BaseModal>
    );
};

export default AddAssetModal;
